import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FaComments, FaFacebook, FaInstagram, FaLinkedin, FaReply, FaSync, FaYoutube } from "react-icons/fa";
import { useNotify } from "../../components/ConfirmModal";
import { Avatar, Empty, PageHead } from "./WorkUi";
import { errorMessage, fmtShortDate } from "./workApi";
import "../dm/dm.css";
import "./work.css";

const ICONS = { Facebook: FaFacebook, Instagram: FaInstagram, LinkedIn: FaLinkedin, YouTube: FaYoutube };
const COMMENTS_INFO =
  "Comments on posts published from Cloud Office appear here within the hour (instantly for Facebook and Instagram when webhooks are on).";
const FILTERS = [
  { key: "New", label: "To answer" },
  { key: "Replied", label: "Answered" },
  { key: "Ignored", label: "Ignored" },
  { key: "all", label: "All" },
];

function CommentRow({ c, api, onChanged }) {
  const notify = useNotify();
  const [reply, setReply] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const Icon = ICONS[c.Platform] || FaComments;

  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.replyComment(c.Comment_Id, reply.trim());
      notify.success(`Replied on ${c.Platform}`);
      setReply("");
      setOpen(false);
      onChanged();
    } catch (err) {
      notify.error(errorMessage(err, "Could not send the reply"));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (Status) => {
    try {
      await api.updateComment(c.Comment_Id, { Status });
      onChanged();
    } catch (err) {
      notify.error(errorMessage(err));
    }
  };

  return (
    <li className="wk-daily-row">
      <div className="wk-daily-row__title" style={{ justifyContent: "space-between" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Avatar name={c.Author_Name} size={26} />
          {c.Author_Name || "Someone"}
          <span className="wk-muted" style={{ fontWeight: 400 }}>
            <Icon aria-label={c.Platform} /> {c.Account_Name} · {c.Commented_At ? fmtShortDate(c.Commented_At) : ""}
          </span>
        </span>
        {c.Item_Title ? <span className="wk-muted" style={{ fontWeight: 400, fontSize: 12 }}>on "{c.Item_Title}"</span> : null}
      </div>
      <div style={{ whiteSpace: "pre-wrap", marginBottom: 8 }}>{c.Message || <span className="wk-muted">(no text)</span>}</div>
      {c.Reply_Text ? (
        <div className="wk-pack-caption" style={{ marginBottom: 8 }}>
          <strong>Your reply:</strong> {c.Reply_Text}
        </div>
      ) : null}
      {c.Status === "New" ? (
        open ? (
          <form className="wk-quick-add" onSubmit={send}>
            <label className="wk-sr-only" htmlFor={`reply-${c.Comment_Id}`}>
              Reply to {c.Author_Name}
            </label>
            <input
              id={`reply-${c.Comment_Id}`}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Write a friendly reply…"
              maxLength={2000}
              autoFocus
            />
            <button type="submit" className="dmp-btn" disabled={busy || !reply.trim()}>
              Send
            </button>
            <button type="button" className="dmp-btn dmp-btn--ghost" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </form>
        ) : (
          <div className="wk-actions-bar" style={{ margin: 0 }}>
            {c.Can_Reply ? (
              <button type="button" className="dmp-btn" onClick={() => setOpen(true)}>
                <FaReply aria-hidden="true" /> Reply
              </button>
            ) : (
              <span className="wk-muted" style={{ fontSize: 12, alignSelf: "center" }}>
                Reply on {c.Platform} directly
              </span>
            )}
            <button type="button" className="dmp-btn dmp-btn--ghost" onClick={() => setStatus("Ignored")}>
              No reply needed
            </button>
          </div>
        )
      ) : c.Status === "Ignored" ? (
        <button type="button" className="dmp-link" onClick={() => setStatus("New")}>
          Move back to "To answer"
        </button>
      ) : null}
    </li>
  );
}

/** Comments on posts published through Cloud Office, with replies from one place. */
function CommentsInbox({ api, dmProjectId, itemId, compact = false }) {
  const notify = useNotify();
  const [status, setStatus] = useState("New");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(
        await api.comments({
          status,
          dm_project_id: dmProjectId || undefined,
          item_id: itemId || undefined,
        })
      );
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load comments"));
    }
  }, [api, status, dmProjectId, itemId]);

  useEffect(() => {
    load();
  }, [load]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const res = await api.syncEngagement({});
      notify.success(`Synced: ${res.new_comments} new comment(s), ${res.new_leads} new lead(s)`);
      load();
    } catch (err) {
      notify.error(errorMessage(err, "Could not sync"));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="wk">
      <div className="wk-cal-toolbar">
        <div className="wk-chip-row" role="group" aria-label="Filter comments" style={{ marginBottom: 0 }}>
          {FILTERS.map((f) => (
            <button key={f.key} type="button" className="wk-chip" aria-pressed={status === f.key} onClick={() => setStatus(f.key)}>
              {f.label}
              {f.key === "New" && data?.new_count ? ` (${data.new_count})` : ""}
            </button>
          ))}
        </div>
        {!compact ? (
          <button type="button" className="dmp-btn dmp-btn--ghost" style={{ marginLeft: "auto" }} onClick={syncNow} disabled={syncing}>
            <FaSync aria-hidden="true" /> {syncing ? "Syncing…" : "Sync now"}
          </button>
        ) : null}
      </div>
      {error ? <div className="dmp-error">{error}</div> : null}
      {!data ? (
        <div className="wk-muted">Loading…</div>
      ) : data.comments.length ? (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {data.comments.map((c) => (
            <CommentRow key={c.Comment_Id} c={c} api={api} onChanged={load} />
          ))}
        </ul>
      ) : (
        <Empty icon={FaComments} title={status === "New" ? "No comments waiting" : "No comments here"} />
      )}
    </div>
  );
}

export function CommentsPage({ api }) {
  const [params] = useSearchParams();
  const itemId = Number(params.get("item")) || null;
  return (
    <div className="dmp wk">
      <PageHead title="Comments" info={COMMENTS_INFO} />
      <div className="dmp-card">
        <CommentsInbox api={api} itemId={itemId} />
      </div>
    </div>
  );
}

export default CommentsInbox;
